import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getFrontendExperiment } from '@atlassian/search-experiment';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { SmartAnswerFooterSection } from '../../ai-answer-dialog/ai-answer-footer/ai-answer-footer';
import {
	getSmartLinksAnalyticsContextData,
	onAIAnswerResultCompleteShown,
	onAIAnswerSourceClick,
	onAIAnswerTabClick,
	SmartLinksAnalyticsContextLocation,
} from '../../ai-answer-dialog/analytics';
import { type NLPSearchResultSource } from '../../ai-answer-dialog/types';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { AISourcesCarousel } from '../../common/ui/ai-sources';
import { getSAINLayerFrontendMutiArmExperiment } from '../../common/utils/get-sain-layer-frontend-experiment';
import { AIAnswerTab, aiAnswerTabsOrder, QueryIntent } from '../../constants';
import { isHello } from '../../utils';
import { useContentTruncationDetector } from '../common/hooks/use-content-truncation-detector';
import { AICollapse } from '../common/ui/ai-collapse';
import { AIDisclaimerAndFeedback } from '../common/ui/ai-disclaimer-and-feedback';
import { AITabs } from '../common/ui/ai-tabs';
import { AITabsHeader, AITabsHeaderWithSourcesStack } from '../common/ui/ai-tabs-header';
import { AITabsPanel } from '../common/ui/ai-tabs-panel';
import { AITabsSources } from '../common/ui/ai-tabs-sources';
import { ReactRenderer } from '../common/ui/react-renderer';
import { ReadMoreButton } from '../common/ui/read-more-button';

import { messages } from './messages';
import { SmartAnswerCardBodyTabAIAnswer } from './smart-answer-card-body-tab-ai-answer';

const styles = cssMap({
	fullHeight: {
		height: '100%',
	},
	sectionContainer: {
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.050'),
	},
	explicitQuestion: {
		minHeight: '350px',
	},
});

export function SmartAnswerCardBodyComplete({
	collapsed,
	onExpandCollapseButtonClick,
	queryIntent,
}: {
	collapsed: boolean;
	onExpandCollapseButtonClick: () => void;
	queryIntent?: QueryIntent;
}) {
	const [selectedTab, setSelectedTab] = useState<AIAnswerTab>(AIAnswerTab.AI_ANSWER);

	const { callbacks, commonAttributes } = useAIAnswerContext();
	const { answerString, cloudId, sources, errorStateSuggestedQueries, searchSessionId } =
		commonAttributes;
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const [fireAnalyticsEvent] = useAnalytics();
	const onHello = isHello(cloudId);

	const renderedContentRef = useRef<HTMLDivElement>(null);
	const contentContainerRef = useRef<HTMLDivElement>(null);
	const completeAnswerAnalyticsEventFiredRef = useRef(false);

	// Detect if content is truncated when collapsed
	const isContentTruncated = useContentTruncationDetector(
		renderedContentRef,
		contentContainerRef,
		collapsed,
	);

	useEffect(() => {
		// Only fire once and ensure DOM element is ready with meaningful content
		if (completeAnswerAnalyticsEventFiredRef.current || !renderedContentRef.current) {
			return;
		}
		const answerHeight = renderedContentRef.current.getBoundingClientRect().height;
		const containerHeight = contentContainerRef.current?.getBoundingClientRect().height ?? 0;
		const whitespaceHeight = Math.max(containerHeight - answerHeight, 0);

		// Only fire if we have meaningful height (content is rendered)
		if (answerHeight > 0 && containerHeight > 0) {
			fireAnalyticsEvent(
				onAIAnswerResultCompleteShown({
					...commonAttributes,
					answerHeight,
					containerHeight,
					whitespaceHeight,
					queryIntent: queryIntent,
				}),
			);
			completeAnswerAnalyticsEventFiredRef.current = true;
		}
	}, [fireAnalyticsEvent, commonAttributes, queryIntent, isContentTruncated]);

	const { serveExperiment: serveErrorStateSuggestedQueriesExp } = getFrontendExperiment({
		abTestId: 'kd_sain_show_no_answer_suggestions_exp',
	});

	const { cohort: cohortSourcesRedesignExperimentPt2 } = getSAINLayerFrontendMutiArmExperiment({
		abTestId: 'kd_sain_sources_redesign_pt_2',
	});

	const showReadMoreButton =
		selectedTab === AIAnswerTab.AI_ANSWER && collapsed && isContentTruncated;

	useEffect(() => {
		fireSAINUsageEvent({ commonAttributes });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleTabChange = useCallback(
		(selectedTabIndex: number) => {
			const selectedTab = aiAnswerTabsOrder[selectedTabIndex];
			setSelectedTab(selectedTab);

			fireAnalyticsEvent(
				onAIAnswerTabClick({
					...commonAttributes,
					selectedTab,
				}),
			);
		},
		[setSelectedTab, fireAnalyticsEvent, commonAttributes],
	);

	const sourceClickCallback = useCallback(
		(index: number, source: NLPSearchResultSource, hostname?: string) => {
			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					sourceIndex: index,
					hostname,
					sourceProduct: source.product || '',
					...commonAttributes,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.SOURCE_CLICKED,
				commonAttributes,
				sourceProduct: source.product || '',
			});
			callbacks?.onAIAnswerSourceClick?.();
		},
		[fireAnalyticsEvent, commonAttributes, callbacks, fireSAINUsageEvent],
	);

	if (onHello || cohortSourcesRedesignExperimentPt2 === 'sources_under_answer') {
		return (
			<Stack
				alignBlock="stretch"
				grow="fill"
				spread="space-between"
				xcss={cx(
					styles.fullHeight,
					queryIntent === QueryIntent.EXPLICIT_QUESTION && styles.explicitQuestion,
				)}
			>
				<Stack alignBlock="stretch" grow="fill" spread="space-between" xcss={styles.fullHeight}>
					<Box>
						<AICollapse
							title={
								<Heading as="h2" size="xxsmall">
									<Text color="color.text.subtlest" size="small" weight="bold">
										<FormattedMessage {...messages.smart_answer_card_title} />
									</Text>
								</Heading>
							}
							isCollapsed={collapsed}
							onExpandCollapse={onExpandCollapseButtonClick}
							ref={contentContainerRef}
							showReadMoreButton={showReadMoreButton}
							queryIntent={queryIntent}
						>
							<AnalyticsContext
								data={getSmartLinksAnalyticsContextData({
									location: SmartLinksAnalyticsContextLocation.SAIN_Q_AND_A_CARD,
									commonAttributes,
								})}
							>
								<SmartCardProvider>
									<Box id="ai-answer-result-section" ref={renderedContentRef}>
										<ReactRenderer markdown={answerString} />
									</Box>
								</SmartCardProvider>
							</AnalyticsContext>
						</AICollapse>
						{showReadMoreButton && <ReadMoreButton onClick={onExpandCollapseButtonClick} />}
					</Box>
					{!collapsed && sources.length > 0 ? (
						<Box xcss={styles.sectionContainer}>
							<AISourcesCarousel
								sources={sources}
								searchSessionId={searchSessionId}
								onClickHandler={sourceClickCallback}
							/>
						</Box>
					) : null}
					<Box xcss={styles.sectionContainer}>
						<AIDisclaimerAndFeedback
							showFeedbackButtons={true}
							showCopyButton={fg('kd_sain_copy_button')}
							renderedContentRef={renderedContentRef}
						/>
					</Box>
				</Stack>
				{!collapsed ||
				(errorStateSuggestedQueries.length > 0 && serveErrorStateSuggestedQueriesExp) ? (
					<SmartAnswerFooterSection showOpenChat={true} />
				) : null}
			</Stack>
		);
	}

	return (
		<Stack
			alignBlock="stretch"
			grow="fill"
			spread="space-between"
			xcss={cx(
				styles.fullHeight,
				queryIntent === QueryIntent.EXPLICIT_QUESTION && styles.explicitQuestion,
			)}
		>
			<Stack alignBlock="stretch" grow="fill" spread="space-between" xcss={styles.fullHeight}>
				<AITabs
					onChange={handleTabChange}
					selectedTabIndex={aiAnswerTabsOrder.indexOf(selectedTab)}
				>
					{cohortSourcesRedesignExperimentPt2 === 'sources_tab_redesign' ? (
						<AITabsHeaderWithSourcesStack
							collapsed={collapsed}
							onExpandCollapseButtonClick={onExpandCollapseButtonClick}
							sources={sources}
						/>
					) : (
						<AITabsHeader
							collapsed={collapsed}
							onExpandCollapseButtonClick={onExpandCollapseButtonClick}
							showSourcesTab={sources.length > 0}
						/>
					)}
					<AITabsPanel
						collapsed={collapsed}
						ref={contentContainerRef}
						showReadMoreButton={showReadMoreButton}
						queryIntent={queryIntent}
					>
						<SmartAnswerCardBodyTabAIAnswer ref={renderedContentRef} />
					</AITabsPanel>
					{sources.length > 0 ? (
						<AITabsPanel
							collapsed={collapsed}
							queryIntent={queryIntent}
							showReadMoreButton={showReadMoreButton}
							ref={contentContainerRef}
						>
							<AITabsSources />
						</AITabsPanel>
					) : null}
				</AITabs>
				{showReadMoreButton && <ReadMoreButton onClick={onExpandCollapseButtonClick} />}
				<Box xcss={styles.sectionContainer}>
					<AIDisclaimerAndFeedback
						showFeedbackButtons={true}
						showCopyButton={fg('kd_sain_copy_button')}
						renderedContentRef={renderedContentRef}
					/>
				</Box>
			</Stack>
			{(!collapsed ||
				(errorStateSuggestedQueries.length > 0 && serveErrorStateSuggestedQueriesExp)) &&
			selectedTab === AIAnswerTab.AI_ANSWER ? (
				<SmartAnswerFooterSection showOpenChat={true} />
			) : null}
		</Stack>
	);
}
